import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { Loader2, RefreshCw } from "lucide-react";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { isStaleBuildError, reloadForNewBuild } from "@/lib/stale-build";

/** Shown instead of React Router's developer error screen when a page fails to load or crashes. */
export function RouteError() {
  const error = useRouteError();
  const stale = isStaleBuildError(error);
  // A new version was deployed while this tab was open: fetch it instead of showing an error.
  const reloading = stale && reloadForNewBuild();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="px-5 py-5 sm:px-8">
        <Link to="/" aria-label="MarkFeed home">
          <Logo />
        </Link>
      </header>
      <main className="flex flex-1 items-center justify-center px-5 pb-20">
        <div className="w-full max-w-md text-center">
          {reloading ? (
            <>
              <Loader2 className="mx-auto size-6 animate-spin text-brand-600" />
              <h1 className="mt-4 text-xl font-semibold text-ink">Loading the latest version…</h1>
              <p className="mt-2 text-slate-600">MarkFeed was just updated. This only takes a second.</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{notFound ? "Page not found" : "Something went wrong"}</h1>
              <p className="mt-3 text-slate-600">
                {notFound
                  ? "This page doesn't exist or has moved."
                  : stale
                    ? "We couldn't load the latest version of this page. Check your connection and reload."
                    : "This page hit an unexpected problem. Reloading usually fixes it. If it keeps happening, let us know."}
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button onClick={() => window.location.reload()}>
                  <RefreshCw /> Reload page
                </Button>
                <Button asChild variant="outline">
                  <Link to="/">Go to home page</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
